import { AnchorLink } from 'components/anchorLink';
import { Icon, IconsEnum } from 'components/Icons';
import { DynamicImage } from 'components/Image';
import { selectAvatar } from 'features/dashboard/selectors';
import { useGetLabelByKey } from 'hooks/useLingUI';
import useWindowSize from 'hooks/useWindowSize';
import { useTypedSelector } from 'lib/centralStore';
import { DashboardCms } from 'lib/datoCms/queries/getDashboardPage';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { isClientSide } from 'utility/constant';
import { isNotEmpty } from 'utility/functions';
import { getRouteByKey, PathName } from 'utility/routes';
import styles from './DashboardUserData.module.scss';

export type UserDataProps = {
  userName: string;
  lastAccess: string;
  data: DataProps[];
};

export type DataProps = {
  label: string;
  value: string;
};

export type DashboardUserDataProps = {
  userData: UserDataProps;
  dashboardPage: DashboardCms;
  myDataPageUrl: string;
  onClick?: () => void;
  containerClassName?: string;
};

const refillUrl = getRouteByKey(PathName.refillUrl);
const withdrawalUrl = getRouteByKey(PathName.withdrawalUrl);

export const DashboardUserData = ({
  userData,
  dashboardPage,
  myDataPageUrl,
  containerClassName,
  onClick,
}: DashboardUserDataProps) => {
  const [userContainerHeight, setUserContainerHeight] = useState<number>();
  const [positionUser, setPositionUser] = useState<number>();
  const labelModificaDati = useGetLabelByKey('modifica-dati');
  const labelRicarica = useGetLabelByKey('ricarica');
  const labelPreleva = useGetLabelByKey('preleva');
  const { isMobile } = useWindowSize();
  const { push } = useRouter();

  const userContainerRef = useRef<HTMLDivElement>(null);
  const buttonContainerRef = useRef<HTMLDivElement>(null);
  const buttonContainerHeight = buttonContainerRef.current ? buttonContainerRef.current.clientHeight : 0;
  const avatar = useTypedSelector(selectAvatar);

  const onScrollHandler = () => {
    const userY = userContainerRef!.current!.getBoundingClientRect().y;
    const height = userContainerRef!.current!.getBoundingClientRect().height;

    setPositionUser(userY);
    setUserContainerHeight(height);
  };

  useEffect(() => {
    if (isClientSide) {
      window.addEventListener('scroll', onScrollHandler, { passive: true });
    }
    return () => {
      if (isClientSide) {
        window.removeEventListener('scroll', onScrollHandler);
      }
    };
  });

  return (
    <div className={`${styles.container} ${containerClassName ? containerClassName : ''}`}>
      <div className={styles.header} ref={userContainerRef}>
        <div className={styles.flex}>
          {isNotEmpty(avatar?.src) ? (
            <div className={styles.avatarIcon}>
              <DynamicImage
                src={`${avatar?.src}`}
                alt="avatar"
                width={32}
                height={32}
                style={{ borderRadius: '50%' }}
                loading="lazy"
                decoding="async"
              />
            </div>
          ) : (
            <div>
              <Icon iconId={IconsEnum.AVATAR} className={styles.avatarIcon} />
            </div>
          )}
          <div>
            <p className={styles.userName}>{userData.userName}</p>
            <p className={styles.access}>
              {dashboardPage?.testoUltimoAccesso} {userData.lastAccess}
            </p>
          </div>
        </div>

        {isMobile ? null : (
          <div>
            <AnchorLink href={myDataPageUrl} className={styles.link}>
              {labelModificaDati}
            </AnchorLink>
          </div>
        )}
      </div>

      {isMobile ? (
        <div
          ref={buttonContainerRef}
          className={positionUser! <= -userContainerHeight! ? styles.buttonContainerFixed : styles.buttonContainer}
        >
          <button
            className={styles.btnPrimary}
            onClick={() => {
              push(refillUrl);
            }}
          >
            {labelRicarica}
          </button>
          <button
            className={styles.btnSecondary}
            onClick={() => {
              push(withdrawalUrl);
            }}
          >
            {labelPreleva}
          </button>
        </div>
      ) : null}

      <div
        style={positionUser! <= -userContainerHeight! ? { marginTop: buttonContainerHeight } : { marginTop: 0 }}
        className={isMobile ? styles.userData : `${styles.userData} ${styles.flex} ${styles.justifyBetween}`}
      >
        {userData.data.map((user, i) => {
          return (
            <div key={i}>
              <p className={styles.label}>{user.label}</p>
              <p className={styles.value}>{user.value}</p>
            </div>
          );
        })}
      </div>

      {isMobile ? (
        <div className={styles.accountSettings}>
          <AnchorLink href={myDataPageUrl} className={styles.link}>
            {labelModificaDati}
          </AnchorLink>
        </div>
      ) : null}
    </div>
  );
};
